import React, { useMemo, useState, useContext } from "react";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Modal } from "react-bootstrap";
import PastoralPagination from "../components/pastoral-care/components/pagination";
import { ProfileContext } from "../contexts/ProfileContext";
import { OrganizationContext } from "../contexts/OrganizationContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  Tooltip,
  Legend,
);

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: false,
    tooltip: {
      callbacks: {
        label: (context) => `${context.raw.toLocaleString()} Calls`,
      },
    },
  },
  scales: {
    y: {
      type: "linear",
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        callback: (value) => (Number.isInteger(value) ? value : null),
      },
    },
  },
};

const BarchartAreacode = ({
  data,
  dateRange,
  showModel,
  inlineMode = false,
  onBarClick = () => {}, // ✅ New prop for initial bar clicks
  onDetailsLoaded = () => {}, // ✅ For drilldown bar clicks
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { getAreaCodeDetails, areaCodeDetails } = useContext(ProfileContext);
  const { organizationNumber } = useContext(OrganizationContext);

  // ✅ Prepare chart data
  const chartData = useMemo(() => {
    const labels = (data || []).map((item) =>
      item.label?.trim() === "" ? "(Unknown)" : item.label,
    );

    const counts = (data || []).map((item) => item.total);

    const barColors = ["#FF7033", "#ec3310", "#2db3c0", "#adcfd4", "#de735e"];
    const backgroundColors = counts.map(
      (_, i) => barColors[i % barColors.length],
    );

    return {
      labels,
      datasets: [
        {
          label: "Calls Report",
          data: counts,
          backgroundColor: backgroundColors,
          borderRadius: 6,
        },
      ],
    };
  }, [data]);

  const handleBarClick = (event, elements) => {
    if (elements?.length > 0) {
      const index = elements[0].index;
      const selectedItem = data[index];

      if (!selectedItem) return;

      const { label, id } = selectedItem;

      // 🔥 Send both label and id to parent
      onBarClick(label, id);
    }
  };

  // ✅ Paginate results for modal
  const paginated = areaCodeDetails?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <>
      <ChartsWrapper className="row m-3">
        <ChartScrollWrapper className="col-md-10 col-sm-12">
          <ChartCanvasWrapper $barCount={chartData.labels?.length}>
            <Bar
              data={chartData}
              options={{
                ...chartOptions,
                onClick: handleBarClick,
              }}
            />
          </ChartCanvasWrapper>
        </ChartScrollWrapper>
      </ChartsWrapper>

      {/* ✅ Only show modal if not inline mode and in drilldown context */}
      {!inlineMode && onDetailsLoaded && (
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          centered
          size="xl"
          backdrop="static"
          dialogClassName="custom-modal"
        >
          <StyledModalContent>
            <StyledModalHeader>
              <h5>Area Code: {selectedCode || "(Unknown)"}</h5>
              <button onClick={() => setShowModal(false)}>&times;</button>
            </StyledModalHeader>

            <StyledModalBody>
              <TableContainer>
                <StyledTable>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Type Of Call</th>
                      <th>Gender</th>
                      <th>Age Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated?.length ? (
                      paginated.map((item, index) => (
                        <tr key={index}>
                          <td>
                            {new Date(item.created_at).toLocaleString("en-US")}
                          </td>
                          <td className="text-capitalize">{item.call_type}</td>
                          <td className="text-capitalize">
                            {item.gender || "—"}
                          </td>
                          <td>{item.age_range || "—"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4">No call records found.</td>
                      </tr>
                    )}
                  </tbody>
                </StyledTable>
              </TableContainer>

              <StyledPagination>
                <PastoralPagination
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  total_count={areaCodeDetails?.length}
                  handlePageChange={setCurrentPage}
                />
              </StyledPagination>
            </StyledModalBody>

            <StyledModalFooter>
              <button onClick={() => setShowModal(false)}>Close</button>
            </StyledModalFooter>
          </StyledModalContent>
        </Modal>
      )}
    </>
  );
};

export default BarchartAreacode;

/* ---------------- Styled Components ---------------- */

const ChartsWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-around;
`;

const ChartScrollWrapper = styled.div`
  overflow-x: auto;
  width: 100%;
  padding-bottom: 10px;
`;

const ChartCanvasWrapper = styled.div`
  min-width: ${({ $barCount }) => ($barCount || 1) * 80}px;
  height: 400px;
  @media (max-width: 768px) {
    min-width: ${({ $barCount }) => ($barCount || 1) * 70}px;
  }
`;

/* Modal styles remain unchanged */
const StyledModalContent = styled.div`
  border-radius: 20px;
  background: white;
  box-shadow: 0 15px 25px rgba(0, 0, 0, 0.1);
  padding: 20px 10px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const StyledModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 30px 0 30px;
  flex-shrink: 0;
  h5 {
    font-weight: 600;
    color: #178b96;
    margin: 0;
  }
  button {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #aaa;
    &:hover {
      color: #000;
    }
  }
`;

const StyledModalBody = styled.div`
  padding: 0 30px 10px 30px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const StyledModalFooter = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 20px;
  flex-shrink: 0;
  button {
    padding: 8px 25px;
    background-color: #178b96;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 500;
    &:hover {
      background-color: #106271;
    }
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  margin-bottom: 20px;
  max-height: 400px;
`;

const StyledTable = styled.table`
  width: 100%;
  font-family: "Space Grotesk", sans-serif;
  border-collapse: collapse;
  min-width: 800px;

  th,
  td {
    text-align: center;
    padding: 12px;
    border-bottom: 1px solid #dee2e6;
    white-space: nowrap;
  }

  thead th {
    background-color: #f2f6f7;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }
`;

const StyledPagination = styled.div`
  display: flex;
  justify-content: center;
  flex-shrink: 0;
`;
